import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeroProps = {
  language: Language;
};
const content = {
  ua: {
    headline: 'CHAMÄLEON',
    subheadline: 'Преміальний бренд покриттів та автохімії',
    description: '60 років досвіду. Присутні у 80 країнах. Професійні рішення для дистрибуції, ритейлу та кузовних сервісів.',
    cta1: 'Переглянути продукцію',
    cta2: 'Стати партнером'
  },
  en: {
    headline: 'CHAMÄLEON',
    subheadline: 'Premium Coatings & Auto Chemistry Brand',
    description: '60 years of experience. Present in 80 countries. Professional solutions for distribution, retail, and body shops.',
    cta1: 'View Products',
    cta2: 'Become a Partner'
  }
};
export function Hero({
  language
}: HeroProps) {
  const t = content[language];
  return <section className="relative min-h-[100dvh] w-full flex flex-col justify-center overflow-hidden bg-chamaleon-black py-24 lg:py-0" data-id="element-143">
      {/* Full Bleed Video Background */}
      <div className="absolute inset-0 z-0" data-id="element-144">
        <video src="https://cdn.magicpatterns.com/uploads/9dHoCtpr3dmfyKCvdPmweg/gyeonheader360protectionii1080p.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-145" />
        {/* Subtle gradients to ensure text and header readability without darkening the whole video */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/60 pointer-events-none" data-id="element-146" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20" data-id="element-147">
        <div className="max-w-2xl" data-id="element-148">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-none mb-4 sm:mb-6 tracking-tight drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]" data-id="element-149">
            {t.headline}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-xl sm:text-2xl lg:text-3xl text-chamaleon-yellow font-medium mb-6 sm:mb-8 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" data-id="element-150">
            {t.subheadline}
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-base sm:text-lg text-white/90 font-medium mb-8 sm:mb-12 leading-relaxed max-w-xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" data-id="element-151">
            {t.description}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row gap-4" data-id="element-152">
            <motion.a href="#products" className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 bg-chamaleon-yellow text-chamaleon-black font-bold rounded-full overflow-hidden text-sm sm:text-base shadow-[0_8px_20px_rgba(255,184,28,0.3)] hover:shadow-[0_8px_25px_rgba(255,184,28,0.5)] transition-all" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-153">
              <span className="relative z-10" data-id="element-154">{t.cta1}</span>
              <ArrowRightIcon className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-155" />
            </motion.a>

            <motion.a href="#partner" className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-md border border-white/20 text-white font-bold rounded-full hover:bg-white/20 hover:border-white/40 transition-all duration-300 text-sm sm:text-base shadow-lg" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-156">
              {t.cta2}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-157" />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>;
}